/*
 * BEDROCK header — hover-to-open, page dim, and the slide-away close for the
 * mega panels.
 *
 * Hover is deliberately NOT a CSS :hover rule. A CSS-only mega menu opens on
 * touch as a side effect of the tap that was meant to follow the link, cannot
 * be closed by Esc, and fights the native <details> state that Dawn's keyboard
 * and click handling depend on. This instead drives the SAME `open` attribute
 * a click drives, so there is exactly one state to reason about.
 *
 * Structure of this file:
 *   1. State reflection — runs on EVERY pointer type. The native `toggle`
 *      event is the funnel every open/close path already passes through
 *      (hover here, click, Esc and focusout in Dawn's details-disclosure), so
 *      body.has-dropdown-menu — which drives the page dim — cannot drift from
 *      the real <details> state. The overlay click closes whatever is open.
 *   2. Animated close — the hover path leaves through a slide-up animation:
 *      `mega-menu--closing` keeps [open] alive while the panel animates, then
 *      the attribute drops. Esc and focusout stay INSTANT by design: they are
 *      Dawn's paths, an interruption is what the user asked for, and racing
 *      details-disclosure's own close would be the only way to animate them.
 *   3. Hover intent — attached only on hover-capable pointers; on touch the
 *      listeners never exist and behaviour is byte-identical to stock.
 */

(() => {
  const menus = Array.from(document.querySelectorAll('.header__inline-menu header-menu'));
  if (!menus.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setOpen = (details, open) => {
    if (details.open === open) return;
    details.open = open;
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  /* Close with the slide-up. Falls back to an instant close when there is no
     panel to animate or the user asked for reduced motion. The timeout is the
     insurance policy: if `animationend` never fires (display cut by another
     state change), the panel must still end CLOSED, never wedged half-open.
     A close in flight is CANCELLABLE — re-hovering mid-animation must keep the
     panel open, not lose to a stale timer 500ms later. */
  const CLOSE_FALLBACK_MS = 500;
  const closingState = new WeakMap();

  const cancelClose = (details) => {
    const state = closingState.get(details);
    if (state) state.cancelled = true;
    closingState.delete(details);
    details.classList.remove('mega-menu--closing');
  };

  const closeAnimated = (details) => {
    const panel = details.querySelector('.mega-menu__panel');
    if (!details.open) return;
    if (!panel || reducedMotion.matches) {
      setOpen(details, false);
      return;
    }
    if (closingState.has(details)) return;

    const state = { cancelled: false };
    closingState.set(details, state);
    details.classList.add('mega-menu--closing');
    const finish = () => {
      if (state.cancelled) return;
      state.cancelled = true;
      closingState.delete(details);
      details.classList.remove('mega-menu--closing');
      setOpen(details, false);
    };
    panel.addEventListener(
      'animationend',
      (event) => {
        if (event.animationName === 'bedrock-panel-out') finish();
      },
      { once: true }
    );
    setTimeout(finish, CLOSE_FALLBACK_MS);
  };

  /* --- 1. State reflection (all pointer types) --- */
  const syncBodyClass = () => {
    const anyOpen = menus.some((menu) => menu.querySelector('details')?.open);
    document.body.classList.toggle('has-dropdown-menu', anyOpen);
  };

  menus.forEach((menu) => {
    menu.querySelector('details')?.addEventListener('toggle', syncBodyClass);
  });

  document.querySelector('.bedrock-header-overlay')?.addEventListener('click', () => {
    menus.forEach((menu) => {
      const details = menu.querySelector('details');
      if (details?.open) closeAnimated(details);
    });
  });

  /* --- 2 + 3. Hover intent (hover-capable pointers only) --- */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (!canHover.matches) return;

  // Intent delay. Without it, dragging the pointer across the nav row on the
  // way somewhere else opens and closes every panel it crosses.
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 150;

  menus.forEach((menu) => {
    const details = menu.querySelector('details');
    if (!details) return;

    let timer;
    const schedule = (open, delay) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Re-checked at fire time, not at schedule time: the pointer may have
        // left again, and focus may have moved in, during the delay.
        if (!open && menu.contains(document.activeElement)) return;
        if (open) {
          cancelClose(details);
          setOpen(details, true);
        } else {
          closeAnimated(details);
        }
      }, delay);
    };

    menu.addEventListener('mouseenter', () => schedule(true, OPEN_DELAY));
    menu.addEventListener('mouseleave', () => schedule(false, CLOSE_DELAY));

    // A click while hover has already opened the panel would otherwise toggle
    // it shut and immediately reopen on the next mouseenter, which reads as a
    // flicker rather than as a close. Cancelling the pending timer lets the
    // native <details> toggle win outright.
    menu.addEventListener('click', () => clearTimeout(timer));
  });
})();
